import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { PublicFooter } from "@/components/legal/public-footer";
import { multiResumeBrand } from "@/utils/multiresume-brand";

import {
  CHECK_AI_RESUME_EDITS_GUIDE_PATH,
  checkAiResumeEditsGuideTitle,
  GUIDES_DIRECTORY_CANONICAL_URL,
  guideTitle,
  guidesDirectoryMessages,
  MANAGE_RESUME_VERSIONS_GUIDE_PATH,
  manageResumeVersionsGuideTitle,
  TAILOR_RESUME_GUIDE_PATH,
} from "./guides-copy";

function messageText(currentI18n: typeof i18n, message: MessageDescriptor) {
  return currentI18n._(message);
}

export function getGuidesDirectoryMetadata() {
  const title = messageText(i18n, guidesDirectoryMessages.title);
  const description = messageText(i18n, guidesDirectoryMessages.metadataDescription);

  return {
    title: `${title} - ${multiResumeBrand.appName}`,
    description,
    ogUrl: GUIDES_DIRECTORY_CANONICAL_URL,
  };
}

export function GuidesDirectory() {
  const { i18n: currentI18n } = useLingui();
  const t = (message: MessageDescriptor) => messageText(currentI18n, message);

  return (
    <main id="main-content" className="min-h-svh bg-background pt-20">
      <section className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-12 lg:py-16">
        <header className="space-y-4 border-b border-border pb-10">
          <h1 className="text-4xl font-semibold tracking-tight text-balance text-foreground sm:text-5xl">
            {t(guidesDirectoryMessages.title)}
          </h1>
          <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">
            {t(guidesDirectoryMessages.intro)}
          </p>
        </header>

        <article className="mt-10 space-y-4 rounded-lg border border-border bg-card p-6 sm:p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            <a
              className="underline decoration-border underline-offset-4"
              href={TAILOR_RESUME_GUIDE_PATH}
              data-phase6-entry="guides-to-tailor-resume"
            >
              {t(guideTitle)}
            </a>
          </h2>
          <p className="leading-relaxed text-muted-foreground">{t(guidesDirectoryMessages.c01Description)}</p>
          <a
            className="inline-flex font-medium text-foreground underline underline-offset-4"
            href={TAILOR_RESUME_GUIDE_PATH}
            data-phase6-entry="guides-to-tailor-resume"
          >
            {t(guidesDirectoryMessages.c01Link)}
          </a>
        </article>

        <article className="space-y-4 rounded-lg border border-border bg-card p-6 sm:p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            <a
              className="underline decoration-border underline-offset-4"
              href={MANAGE_RESUME_VERSIONS_GUIDE_PATH}
              data-phase6-entry="guides-to-manage-resume-versions"
            >
              {t(manageResumeVersionsGuideTitle)}
            </a>
          </h2>
          <p className="leading-relaxed text-muted-foreground">{t(guidesDirectoryMessages.c02Description)}</p>
          <a
            className="inline-flex font-medium text-foreground underline underline-offset-4"
            href={MANAGE_RESUME_VERSIONS_GUIDE_PATH}
            data-phase6-entry="guides-to-manage-resume-versions"
          >
            {t(guidesDirectoryMessages.c02Link)}
          </a>
        </article>

        <article className="space-y-4 rounded-lg border border-border bg-card p-6 sm:p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            <a
              className="underline decoration-border underline-offset-4"
              href={CHECK_AI_RESUME_EDITS_GUIDE_PATH}
              data-phase6-entry="guides-to-check-ai-resume-edits"
            >
              {t(checkAiResumeEditsGuideTitle)}
            </a>
          </h2>
          <p className="leading-relaxed text-muted-foreground">{t(guidesDirectoryMessages.c03Description)}</p>
          <a
            className="inline-flex font-medium text-foreground underline underline-offset-4"
            href={CHECK_AI_RESUME_EDITS_GUIDE_PATH}
            data-phase6-entry="guides-to-check-ai-resume-edits"
          >
            {t(guidesDirectoryMessages.c03Link)}
          </a>
        </article>
      </section>

      <PublicFooter />
    </main>
  );
}

export { guidesDirectoryMessages };
